<template>
  <v-sheet class="custom-timetable">
    <v-toolbar flat class="calendar-header flex-md-column" height="auto">
      <v-toolbar-items class="my-2 toolbar-left-items flex-grow-1 align-center flex-sm-row flex-column">
        <div class="d-flex align-center">
          <v-btn
            text
            depressed
            icon
            class="my-auto mx-1"
            @click="_onDateControlClick('prev')"
            :class="{ 'pointer-loading': loading }"
          >
            <v-icon class="fi fi-rr-angle-small-left" color="colorWhite" size="20"></v-icon>
          </v-btn>
          <v-btn
            text
            depressed
            icon
            class="my-auto mx-1"
            @click="_onDateControlClick('next')"
            :class="{ 'pointer-loading': loading }"
          >
            <v-icon class="fi fi-rr-angle-small-right" color="colorWhite" size="20"></v-icon>
          </v-btn>
        </div>

        <v-menu
          ref="datePickerMenu"
          v-model="datePickerOpen"
          transition="scale-transition"
          offset-y
          min-width="auto"
          :close-on-content-click="false"
          :return-value.sync="pickerDate"
          :disabled="loading"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              depressed
              color="colorWhite"
              class="subtitle-2 px-2"
              :class="{ 'pointer-loading': loading }"
              outlined
            >
              {{ dateDisplayText }}
            </v-btn>
          </template>

          <v-date-picker
            no-title
            scrollable
            v-model="pickerDate"
            :first-day-of-week="startWeekday"
            :day-format="$formatter.dayFormat"
            :locale="locale"
            :type="calendarType === 'month' ? 'month' : 'date'"
            :range="calendarType === 'week'"
            :key="calendarType === 'month' ? 'month' : 'date'"
            :readonly="loading"
            @click:date="_onPickerDateClick($event)"
            :picker-date.sync="pickActiveDate"
          >
            <v-spacer></v-spacer>
            <div class="d-flex justify-space-between" style="width: 100%;">
              <v-btn min-width="max-content" depressed small text color="error" @click.prevent="datePickerOpen = false">
                <v-icon class="mr-1 fi fi-rr-cross-small" small></v-icon>{{ $t('cancel') }}
              </v-btn>
              <v-btn
                min-width="max-content"
                depressed
                small
                text
                color="primary"
                @click.prevent="_handleDatePickerSubmit()"
              >
                <v-icon class="mr-1 fi fi-rr-check" size="12"></v-icon>{{ $t('confirm') }}
              </v-btn>
            </div>
          </v-date-picker>
        </v-menu>

        <v-btn
          v-if="enableCreateButton && calendarType === 'day'"
          outlined
          depressed
          dark
          small
          class="ml-4 my-1"
          @click.prevent="_onCreateClick()"
        >
          <v-icon class="fi fi-rr-add mr-1" size="15"></v-icon>{{ $t(createButtonText) }}
        </v-btn>
      </v-toolbar-items>
      <v-spacer></v-spacer>
      <v-toolbar-items class="my-2 toolbar-right-items align-center flex-column-reverse flex-md-row">
        <slot name="toolbar-right"></slot>

        <v-switch
          v-if="enableExpand"
          v-model="expandStudent"
          inset
          :label="$t('expandStudentList')"
          color="accent"
          dark
          hide-details
          dense
          class="my-4 my-md-0"
          @change="_onExpandStudentChanged(false)"
          :readonly="loading"
        ></v-switch>

        <div class="d-flex align-center">
          <v-btn
            v-for="value in ['day', 'week', 'month']"
            :key="`btn-control-${value}`"
            @click="_onCalendarTypeChanged(value)"
            class="my-auto ml-3"
            :class="{ accent: calendarType === value, 'pointer-loading': loading }"
            :color="calendarType === value ? 'colorBlack' : 'colorWhite'"
            outlined
          >
            {{ $t(`calendar.control.${value}`) }}
          </v-btn>
        </div>
      </v-toolbar-items>
    </v-toolbar>

    <v-container class="custom-timetable__body pa-0" v-if="loading">
      <v-skeleton-loader type="table-tbody"></v-skeleton-loader>
    </v-container>
    <template v-else>
      <v-container v-if="calendarType === 'day'" id="day-calendar-container" fluid class="custom-timetable__body  pa-0">
        <v-calendar
          v-if="!displayEmpty"
          ref="dayCalendar"
          type="day"
          :show-month-on-first="false"
          :weekday-format="_displayCalendarWeekday"
          :day-format="_displayCalendarDay"
          :events="calendarEvents"
          v-model="calendarValue"
          :event-more="false"
          :locale="locale"
          event-overlap-mode="column"
          :first-time="$settings.startTime"
          :interval-count="intervalCount"
          :interval-format="_displayCalendarInterval"
          :interval-height="intervalH"
          :interval-minutes="interval"
          hide-header
          @click:event="_onCalendarEventClick($event)"
          :show-interval-label="() => true"
        >
          <template v-slot:event="{ event }">
            <div :id="`daily_${event.id}`" :data-start="event.start_time" :data-end="event.end_time">
              <slot name="event" :event="event" :expand="expandStudent"></slot>
            </div>
          </template>
          <template v-slot:day-body="{ date }">
            <div v-if="_isToday(date)" class="v-current-time first" :style="{ top: currentY }"></div>
          </template>
        </v-calendar>
        <div v-else class="text-center colorBlack--text d-flex align-center justify-center" style="min-height: 180px;">
          {{ $t(noDataText) }}
        </div>
      </v-container>
      <v-container v-else-if="calendarType === 'week_temp'" fluid class="custom-timetable__body pa-0">
        <table v-if="!displayEmpty || enableCreateButton" class="d-flex">
          <tr>
            <th class="custom-timetable__col"></th>
            <th v-for="(date, index) in datesList" :key="`week-temp-${date}-${index}`" class="custom-timetable__col">
              <div class="custom-timetable__col__header">
                <div class="caption mb-1">{{ _formatWeekday(date) }}</div>
                <v-btn
                  text
                  small
                  class="body-2"
                  :class="{ 'holiday--text': _isHoliday(date) }"
                  @click.prevent="_onWeekdayCalendarDateClick(date)"
                >
                  {{ _formateShortDay(date) }}
                </v-btn>
                <div v-if="_getHolidayName(date)" class="holiday-name holiday--text mb-1 text-center">
                  {{ _getHolidayName(date) }}
                </div>
                <v-btn
                  v-if="enableCreateButton"
                  outlined
                  depressed
                  class="my-1 d-block mx-auto"
                  color="primary"
                  @click.prevent="_onCreateClick(date)"
                  block
                  small
                >
                  <v-icon class="fi fi-rr-add mr-1" size="15"></v-icon>
                  {{ $t(createButtonText) }}
                </v-btn>
              </div>
            </th>
          </tr>
          <tr>
          </tr>
          <!-- <div v-for="(date, index) in datesList" class="custom-timetable__col">
            0
            <div class="custom-timetable__col__header">
              <div class="caption mb-1">{{ _formatWeekday(date) }}</div>
              <v-btn
                text
                small
                class="body-2"
                :class="{ 'holiday--text': _isHoliday(date) }"
                @click.prevent="_onWeekdayCalendarDateClick(date)"
              >
                {{ _formateShortDay(date) }}
              </v-btn>
              <div v-if="_getHolidayName(date)" class="holiday-name holiday--text mb-1 text-center">
                {{ _getHolidayName(date) }}
              </div>
              <v-btn
                v-if="enableCreateButton"
                outlined
                depressed
                class="my-1 d-block mx-auto"
                color="primary"
                @click.prevent="_onCreateClick(date)"
                block
                small
              >
                <v-icon class="fi fi-rr-add mr-1" size="15"></v-icon>
                {{ $t(createButtonText) }}
              </v-btn>
            </div>
            <div class="custom-timetable__col__body">
              <div v-for="rows in weeklyTableData[index]" class="custom-timetable__col-row d-flex">
                2
                <div
                  v-for="item in rows.lessons"
                  @click="_navToDetails(item.id)"
                  class="mb-1 custom-timetable__week-event"
                  :class="{ squeeze: rows.length > 1 }"
                >
                4
                  <slot name="event" :event="item" :expand="expandStudent"></slot>
                </div>
              </div>
            </div>
          </div> -->
        </table>
        <div v-else class="text-center colorBlack--text d-flex align-center justify-center" style="min-height: 180px;">
          {{ $t(noDataText) }}
        </div>
      </v-container>
      <v-container v-else-if="calendarType === 'week'" fluid class="custom-timetable__body pa-0 week-calendar">
        <div v-if="!displayEmpty || enableCreateButton" class="custom-timetable__week-grid">
          <div class="custom-timetable__week-grid__header">
            <div class="custom-timetable__week-grid__time-col"></div>
            <div v-for="(date, index) in datesList" :key="`week-${date}-${index}`" class="custom-timetable__col">
              <div class="custom-timetable__col__header">
                <div class="caption mb-1">{{ _formatWeekday(date) }}</div>
                <div class="v-calendar-weekly__day-label">
                  <v-btn
                    small
                    fab
                    depressed
                    @click.prevent="_onWeekdayCalendarDateClick(date)"
                    :class="{ 'holiday--text': _isHoliday(date) }"
                  >
                    {{ _displayDayNumber(date) }}
                  </v-btn>
                </div>
                <div v-if="_getHolidayName(date)" class="holiday-name holiday--text mb-1 text-center">
                  {{ _getHolidayName(date) }}
                </div>
                <v-btn
                  v-if="enableCreateButton"
                  outlined
                  depressed
                  class="my-1 d-block mx-auto"
                  color="primary"
                  @click.prevent="_onCreateClick(date)"
                  block
                  small
                >
                  <v-icon class="fi fi-rr-add mr-1" size="15"></v-icon>
                  {{ $t(createButtonText) }}
                </v-btn>
              </div>
            </div>
          </div>
          <div class="custom-timetable__week-grid__body">
            <div v-for="(timeSlot, slotIndex) in $calendarTimes" :key="`week-slot-${slotIndex}`" class="custom-timetable__week-grid__row">
              <div class="custom-timetable__week-grid__time-col">
                <div class="custom-timetable__week-grid__time-start">{{ timeSlot.start_time }}</div>
                <div class="custom-timetable__week-grid__time-end">{{ timeSlot.end_time }}</div>
              </div>
              <div v-for="(date, dateIndex) in datesList" :key="`week-cell-${date}-${slotIndex}`" class="custom-timetable__week-grid__cell">
                <div
                  v-for="item in weeklyTableData[dateIndex][slotIndex].lessons"
                  :key="`week-item-${item.id}`"
                  @click="_navToDetails(item.id)"
                  :class="[
                    'mb-1', 'custom-timetable__week-event',
                    { 'disable-hidden': expandStudent }
                  ]"
                >
                  <!-- class="mb-1 custom-timetable__week-event" -->
                  <slot name="event" :event="item" :expand="expandStudent"></slot>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center colorBlack--text d-flex align-center justify-center" style="min-height: 180px;">
          {{ $t(noDataText) }}
        </div>
      </v-container>
      <!-- <v-container v-else-if="calendarType === 'week'" fluid class="custom-timetable__body pa-0">
        <div v-if="!displayEmpty || enableCreateButton" class="d-flex">
          <div v-for="(date, index) in datesList" class="custom-timetable__col">
            0
            <div class="custom-timetable__col__header">
              <div class="caption mb-1">{{ _formatWeekday(date) }}</div>
              <v-btn
                text
                small
                class="body-2"
                :class="{ 'holiday--text': _isHoliday(date) }"
                @click.prevent="_onWeekdayCalendarDateClick(date)"
              >
                {{ _formateShortDay(date) }}
              </v-btn>
              <div v-if="_getHolidayName(date)" class="holiday-name holiday--text mb-1 text-center">
                {{ _getHolidayName(date) }}
              </div>
              <v-btn
                v-if="enableCreateButton"
                outlined
                depressed
                class="my-1 d-block mx-auto"
                color="primary"
                @click.prevent="_onCreateClick(date)"
                block
                small
              >
                <v-icon class="fi fi-rr-add mr-1" size="15"></v-icon>
                {{ $t(createButtonText) }}
              </v-btn>
            </div>
            <div class="custom-timetable__col__body">
              <div v-for="rows in weeklyTableData[index]" class="custom-timetable__col-row d-flex">
                2
                <div
                  v-for="item in rows.lessons"
                  @click="_navToDetails(item.id)"
                  class="mb-1 custom-timetable__week-event"
                  :class="{ squeeze: rows.length > 1 }"
                >
                4
                  <slot name="event" :event="item" :expand="expandStudent"></slot>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center colorBlack--text d-flex align-center justify-center" style="min-height: 180px;">
          {{ $t(noDataText) }}
        </div>
      </v-container> -->
      <v-container v-else-if="calendarType === 'month'" fluid class="custom-timetable__body pa-0">
        <v-calendar
          ref="monthCalendar"
          type="month"
          :show-month-on-first="false"
          :weekday-format="_displayCalendarWeekday"
          :day-format="_displayCalendarDay"
          :events="calendarEvents"
          :event-more="false"
          @click:date="_onMonthCalendarDateClick"
          v-model="calendarValue"
          :locale="locale"
          @click:event="_onCalendarEventClick($event)"
          :weekdays="weekdays"
        >
          <template v-slot:event="{ event }">
            <slot name="event" :event="event" :expand="expandStudent"></slot>
          </template>
          <template v-slot:day-label="{ date, day }">
            <div class="v-calendar-weekly__day-label">
              <v-btn
                small
                fab
                depressed
                @click.prevent="_onWeekdayCalendarDateClick(date)"
                :class="{ 'holiday--text': _isHoliday(date) }"
              >
                {{ day }}
              </v-btn>
            </div>
            <div v-if="_getHolidayName(date)" class="holiday-name holiday--text mb-1 text-center">
              {{ _getHolidayName(date) }}
            </div>
            <div class="px-2" v-if="enableCreateButton">
              <v-btn
                outlined
                depressed
                class="my-1 d-block mx-auto"
                color="primary"
                @click.prevent="_onCreateClick(date)"
                block
                small
              >
                <v-icon class="fi fi-rr-add mr-1" size="15"></v-icon>
                {{ $t(createButtonText) }}
              </v-btn>
            </div>
          </template>
        </v-calendar>
      </v-container>
    </template>
  </v-sheet>
</template>

<script>
export default {
  name: 'CustomTimetable',
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    timetableData: {
      type: Array,
      required: true,
      default: () => [],
    },
    holidayData: {
      type: Array,
      required: false,
      default: () => [],
    },
    noDataText: {
      type: String,
      required: false,
      default: 'table.noData',
    },
    enableExpand: {
      type: Boolean,
      default: false,
      required: false,
    },
    enableCreateButton: {
      type: Boolean,
      default: false,
      required: false,
    },
    createButtonText: {
      type: String,
      required: false,
      default: 'create',
    },
  },
  watch: {
    calendarEvents: {
      handler(val, old) {
        if (this.calendarType === 'day') {
          let jump = false

          if (val.length) {
            const columns = []
            val.forEach(event => {
              let added = false
              for (let i = 0; i < columns.length; i++) {
                let conflict = false
                for (let x = 0; x < columns[i].length; x++) {
                  if (
                    this.$validate.isTimeEqual(event.start, columns[i][x].start) ||
                    (this.$validate.isTimeGreater(event.start, columns[i][x].start) &&
                      this.$validate.isTimeLess(event.start, columns[i][x].end))
                  ) {
                    conflict = true
                    break
                  }
                }
                if (!conflict) {
                  columns[i].push(event)
                  columns[i].sort((a, b) => this._compareEventTime(a, b))
                  added = true
                  break
                }
              }
              if (!added) {
                columns.push([event])
              }
            })

            setTimeout(() => {
              const eventW = 185
              for (let i = 0; i < columns.length; i++) {
                for (let x = 0; x < columns[i].length; x++) {
                  const node = document.getElementById(`daily_${columns[i][x].id}`)
                  if (node) {
                    const parent = node.closest('.v-event-timed')
                    if (parent) {
                      parent.style.left = `${eventW * i}px`
                    }
                  }
                }
              }
            }, 100)

            if (val.length !== (old || []).length) {
              jump = true
            } else {
              for (let i = 0; i < val.length; i++) {
                const found = (old || []).find(el => el.id === val[i].id)
                if (!found) {
                  jump = true
                  break
                }
              }
            }
          } else {
            jump = true
          }

          this._onExpandStudentChanged(jump)
          return
        }

        if (this.calendarType === 'week') {
          this.$nextTick(() => {
            setTimeout(() => {
              this._onExpandStudentChanged(false)
              // this._layoutWeekEvents()
              this._updateWeekColumnWidths()
              this._syncWeekHeaderScroll()
            }, 120)
          })
        }
      },
      deep: true,
      immediate: true,
    },
    calendarValue(val) {
      this.pickActiveDate = val
    },
    weeklyTableData: {
      handler(val) {
        if (this.calendarType === 'week') {
          setTimeout(() => {
            const headers = document.querySelectorAll('.custom-timetable__col__header')
            let maxH = 0
            headers.forEach(node => {
              maxH = Math.max(maxH, node.clientHeight)
            })
            headers.forEach(node => {
              node.style.height = `${maxH}px`
            })
          }, 100)
        }
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    locale() {
      switch (this.$i18n.locale) {
        case 'en':
          return 'en'
        case 'cn':
          return 'zh-CN'
        default:
          return 'zh-HK'
      }
    },
    dateDisplayText() {
      if (this.$validate.DataValid(this.datesList)) {
        if (this.calendarType === 'week') {
          return `${this.$d(this.$formatter.convertDate(this.datesList[0]), 'fullDate')}${
            this.$i18n.locale === 'en' ? ' to ' : '至'
          }${this.$d(this.$formatter.convertDate(this.datesList[this.datesList.length - 1]), 'fullDate')}`
        } else if (this.calendarType === 'month') {
          return this.$d(this.$formatter.convertDate(this.datesList[0]), 'yearMonth')
        } else {
          return this.$d(this.$formatter.convertDate(this.datesList[0]), 'fullDate')
        }
      }

      if (this.calendarType === 'month') {
        return this.$d(this.$formatter.convertDate(), 'yearMonth')
      }

      return this.$d(this.$formatter.convertDate(), 'fullDate')
    },
    todayStr() {
      return this.$formatter.formatDate()
    },
    intervalCount() {
      const startTime = this.$formatter.convertDate('2023-01-01', this.$settings.startTime)
      const endTime = this.$formatter.convertDate('2023-01-01', this.$settings.endTime)
      const diff = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * this.interval)
      return diff
    },
    displayEmpty() {
      return this.calendarEvents.length < 1
    },
    holidayDates() {
      if (this.$validate.DataValid(this.holidayData)) {
        return this.holidayData.map(el => el.date)
      }
      return []
    },
    calendarEvents() {
      let data = this.timetableData
      if (this.calendarType === 'day') {
        data = this.timetableData.filter(el => el.date === this.calendarValue)
      } else if (this.calendarType === 'week') {
        data = this.timetableData.filter(el => {
          return this.datesList.includes(el.date)
        })
      }

      const list = data.map(item => {
        return {
          ...item,
          start: this.$formatter.convertDate(item.date, item.start_time),
          end: this.$formatter.convertDate(item.date, item.end_time),
          timed: true,
        }
      })
      list.sort((a, b) => this._compareEventTime(a, b))

      const group = []
      list.forEach(item => {
        let added = false
        for (let i = 0; i < group.length; i++) {
          if (group[i][0].date === item.date && group[i][0].start_time === item.start_time) {
            group[i].push(item)
            added = true
            break
          }
        }

        if (!added) {
          group.push([item])
        }
      })

      let l = []
      group.forEach(items => {
        items.sort((a, b) => {
          if (a.course_name && b.course_name) {
            if (a.course_name < b.course_name) return -1
            if (a.course_name > b.course_name) return 1
          }

          return 0
        })

        l = [...l, ...items]
      })
      return l
    },
    weeklyTableData() {
      if (this.calendarType === 'week') {
        const list = []
        this.datesList.forEach(date => {
          const events = this.$calendarTimes.map(timeSlot => ({
            start_time: timeSlot.start_time,
            end_time: timeSlot.end_time,
            lessons: [],
          }))
          const inDateData = this.calendarEvents
            .filter(el => el.date === date)
            .sort((a, b) => this._compareEventTime(a, b))
          inDateData.forEach(event => {
            const eventStart = this._timeToMinutes(event.start_time)

            for (let i = 0; i < events.length; i++) {
              const slotStart = this._timeToMinutes(events[i].start_time)
              const slotEnd = this._timeToMinutes(events[i].end_time)

              // Use [start, end) so exact boundary times map to the next slot.
              if (
                eventStart !== null &&
                slotStart !== null &&
                slotEnd !== null &&
                slotStart <= eventStart &&
                eventStart < slotEnd
              ) {
                events[i].lessons.push(event)
                break
              }
            }
          })
          list.push(events)
        })
        return list
      }

      return []
    },
    startWeekday() {
      if (this.isAppMode(['T40'])) {
        return 1
      }
      return 0
    },
    weekdays() {
      const w = []
      for (let i = this.startWeekday; i < 7; i++) {
        w.push(i)
      }

      if (this.startWeekday !== 0) {
        for (let i = 0; i < this.startWeekday; i++) {
          w.push(i)
        }
      }
      return w
    },
    weekColumnCountMap() {
      const map = {}

      if (this.calendarType !== 'week') return map

      this.datesList.forEach(date => {
        const events = this.calendarEvents
          .filter(item => item.date === date)
          .sort((a, b) => this._compareEventTime(a, b))

        const columns = []

        events.forEach(event => {
          let placed = false

          for (let i = 0; i < columns.length; i++) {
            let hasConflict = false

            for (let j = 0; j < columns[i].length; j++) {
              const existing = columns[i][j]

              const overlap =
                event.start.getTime() < existing.end.getTime() &&
                event.end.getTime() > existing.start.getTime()

              if (overlap) {
                hasConflict = true
                break
              }
            }

            if (!hasConflict) {
              columns[i].push(event)
              placed = true
              break
            }
          }

          if (!placed) {
            columns.push([event])
          }
        })

        map[date] = Math.max(columns.length, 1)
      })

      return map
    },
  },
  data: () => ({
    interval: 60, // minute
    // intervalH: 20,
    dayIntervalH: 96,
    weekIntervalH: 72,
    intervalH: 96,
    currentY: '-10px',
    dayCalendarInterval: null,
    weekScrollHandler: null,
    weekScrollTarget: null,

    calendarType: 'day', // 'day' | 'week' | 'month'
    calendarValue: '', // YYYY-MM-DD
    datesList: [],

    expandStudent: true,

    datePickerOpen: false,
    pickerDate: '',
    pickActiveDate: '',
  }),
  methods: {
    setDateList(list) {
      this.datesList = list
      this._updatePickerDate()
    },
    setCurrentValue(value = '') {
      this.calendarValue = value || this.todayStr
    },
    setCalendarType(value) {
      this.calendarType = value
    },
    getDatesList(date = '') {
      const _date = this.$validate.DataValid(date) ? date : this.calendarValue
      const currentDate = this.$formatter.convertDate(_date)
      let tempDatesList = []
      if (this.calendarType === 'day') {
        tempDatesList = [_date]
      } else if (this.calendarType === 'week') {
        const currentWeekday = currentDate.getDay()
        const firstDateOfWeek = this.$formatter.convertDate(_date)
        firstDateOfWeek.setDate(currentDate.getDate() - currentWeekday + this.startWeekday)
        for (let i = 0; i < 7; i++) {
          tempDatesList.push(this.$formatter.formatDate(firstDateOfWeek))
          firstDateOfWeek.setDate(firstDateOfWeek.getDate() + 1)
        }
      } else if (this.calendarType === 'month') {
        const currentMonth = currentDate.getMonth()
        const s = `${currentDate.getFullYear()}-${this.$formatter.pad(currentDate.getMonth() + 1)}-01`
        let firstDateOfMonth = this.$formatter.convertDate(s)
        while (
          firstDateOfMonth.getFullYear() === currentDate.getFullYear() &&
          firstDateOfMonth.getMonth() === currentMonth
        ) {
          tempDatesList.push(this.$formatter.formatDate(firstDateOfMonth))
          firstDateOfMonth.setDate(firstDateOfMonth.getDate() + 1)
        }
      }
      return tempDatesList
    },
    getTodayStr() {
      return this.todayStr
    },

    _onCalendarTypeChanged(type) {
      if (this.loading || this.calendarType === type) return

      if (type === 'week') {
        this.pickerDate = []
      } else {
        this.pickerDate = ''
      }

      if (type === 'day') {
        this.intervalH = 96
      } else if (type === 'week') {
        this.intervalH = 72
      }

      this.calendarType = type
      let currentDateStr = this.todayStr
      if (this.$validate.DataValid(this.calendarValue)) {
        currentDateStr = this.calendarValue
      }

      const currentDate = this.$formatter.convertDate(currentDateStr)
      this.calendarValue = this.$formatter.formatDate(currentDate)
      this.datesList = this.getDatesList()
      this._updatePickerDate()
      this.$emit('type-update', this.calendarType)
      this.$emit('dates-update', this.datesList)
      if (this.calendarType === 'week') {
        this.$nextTick(() => {
          setTimeout(() => {
            this._onExpandStudentChanged(false)
            // this._layoutWeekEvents()
            this._updateWeekColumnWidths()
            this._syncWeekHeaderScroll()
          }, 120)
        })
      }
    },
    _onDateControlClick(action) {
      if (this.loading) return
      let currentDateStr = this.todayStr
      if (this.$validate.DataValid(this.calendarValue)) {
        currentDateStr = this.calendarValue
      }

      const currentDate = this.$formatter.convertDate(currentDateStr)
      this.datesList = []
      if (this.calendarType === 'day') {
        if (action === 'prev') {
          currentDate.setDate(currentDate.getDate() - 1)
          if (this.$refs.dayCalendar) {
            this.$refs.dayCalendar.prev()
          }
        } else {
          currentDate.setDate(currentDate.getDate() + 1)
          if (this.$refs.dayCalendar) {
            this.$refs.dayCalendar.next()
          }
        }

        this.calendarValue = this.$formatter.formatDate(currentDate)
      } else if (this.calendarType === 'week') {
        const currentWeekday = currentDate.getDay()
        if (action === 'prev') {
          const lastDateOfPrevWeek = this.$formatter.convertDate(currentDateStr)
          lastDateOfPrevWeek.setDate(currentDate.getDate() - currentWeekday - 1)
          this.calendarValue = this.$formatter.formatDate(lastDateOfPrevWeek)
        } else {
          const lastDateOfNextWeek = this.$formatter.convertDate(currentDateStr)
          lastDateOfNextWeek.setDate(currentDate.getDate() + (6 - currentWeekday) + 7)
          this.calendarValue = this.$formatter.formatDate(lastDateOfNextWeek)
        }
      } else if (this.calendarType === 'month') {
        if (action === 'prev') {
          const dateOfPrevMonth = this.$formatter.convertDate(currentDateStr)
          dateOfPrevMonth.setMonth(currentDate.getMonth() - 1)
          this.calendarValue = this.$formatter.formatDate(dateOfPrevMonth)
          this.$refs.monthCalendar.prev()
        } else {
          const dateOfNextMonth = this.$formatter.convertDate(currentDateStr)
          dateOfNextMonth.setMonth(currentDate.getMonth() + 1)
          this.calendarValue = this.$formatter.formatDate(dateOfNextMonth)
          this.$refs.monthCalendar.next()
        }
      }

      this.datesList = this.getDatesList()
      this._updatePickerDate()
      this.$emit('dates-update', this.datesList)

      if (this.calendarType === 'week') {
        this.$nextTick(() => {
          setTimeout(() => {
            this._onExpandStudentChanged(false)
            // this._layoutWeekEvents()
            this._updateWeekColumnWidths()
            this._syncWeekHeaderScroll()
          }, 120)
        })
      }
    },
    _onMonthCalendarDateClick(details) {
      this.calendarValue = details.date
      this._onCalendarTypeChanged('day')
    },
    _onWeekdayCalendarDateClick(date) {
      this.calendarValue = date
      this._onCalendarTypeChanged('day')
    },
    _onCalendarEventClick(ev) {
      if (ev && ev.event) {
        this._navToDetails(ev.event.id)
      }
    },
    _formatWeekday(d) {
      return this.$d(this.$formatter.convertDate(d), 'weekday')
    },
    _formateShortDay(d) {
      const dDate = this.$formatter.convertDate(d)
      return `${this.$formatter.pad(dDate.getDate())} - ${this.$formatter.pad(dDate.getMonth() + 1)}`
    },
    _displayDayNumber(d) {
      return this.$formatter.convertDate(d).getDate()
    },
    _displayCalendarWeekday(d) {
      return this.$d(this.$formatter.convertDate(d.date), 'weekday')
    },
    _displayCalendarDay(d) {
      return this.$formatter.convertDate(d.date).getDate()
    },
    _displayCalendarInterval(d) {
      return d.time
    },
    _timeToMinutes(time) {
      if (!this.$validate.DataValid(time)) return null

      const [hoursStr, minutesStr] = String(time).split(':')
      const hours = parseInt(hoursStr, 10)
      const minutes = parseInt(minutesStr, 10)

      if (Number.isNaN(hours) || Number.isNaN(minutes)) return null

      return hours * 60 + minutes
    },
    _compareEventTime(a, b) {
      const startCompare = this.$formatter.sortDate(`${a.date}T${a.start_time}:00Z`, `${b.date}T${b.start_time}:00Z`)
      if (startCompare !== 0) return startCompare

      return this.$formatter.sortDate(`${a.date}T${a.end_time}:00Z`, `${b.date}T${b.end_time}:00Z`)
    },
    _isHoliday(d) {
      return this.holidayDates.includes(d)
    },
    _getHolidayName(d) {
      const found = this.holidayData.find(el => el.date === d)
      if (found) {
        return found.name
      }
      return ''
    },

    _onCreateClick(date = null) {
      let d = date
      if (date === null) {
        if (this.$validate.DataValid(this.datesList)) {
          d = this.datesList[0]
        } else {
          d = this.todayStr
        }
      }

      this.$emit('create-clicked', d)
    },
    _navToDetails(id) {
      this.$emit('event-clicked', id)
    },

    _updatePickerDate() {
      if (this.$validate.DataValid(this.datesList, true)) {
        if (this.calendarType === 'day') {
          this.pickerDate = this.datesList[0]
        } else if (this.calendarType === 'week') {
          this.pickerDate = this.datesList
        } else if (this.calendarType === 'month') {
          this.pickerDate = this.datesList[0].substring(0, 7)
        }
      }
      this.$refs.datePickerMenu.save(this.pickerDate)
    },
    _handleDatePickerSubmit() {
      if (this.loading || !this.$validate.DataValid(this.pickerDate)) return

      this.datesList = []
      if (this.calendarType === 'day') {
        this.calendarValue = this.pickerDate
      } else if (this.calendarType === 'week') {
        this.calendarValue = this.pickerDate[this.pickerDate.length - 1]
      } else if (this.calendarType === 'month') {
        this.calendarValue = `${this.pickerDate}-01`
      }
      this.datesList = this.getDatesList()
      this._updatePickerDate()
      this.$emit('dates-update', this.datesList)
      if (this.calendarType === 'week') {
        this.$nextTick(() => {
          setTimeout(() => {
            this._onExpandStudentChanged(false)
            // this._layoutWeekEvents()
            this._updateWeekColumnWidths()
            this._syncWeekHeaderScroll()
          }, 120)
        })
      }
    },
    _onPickerDateClick(date) {
      if (this.calendarType === 'week') {
        this.pickerDate = this.getDatesList(date)
      }
    },

    _onExpandStudentChanged(jump = true) {
      // if (this.calendarType === 'day') {
      //   this.intervalH = 96

      //   this.$nextTick(() => {
      //     setTimeout(() => {
      //       this._updateTimeline(jump)
      //     }, 100)
      //   })

      //   return
      // }

      // if (this.calendarType === 'week') {
      //   this.intervalH = 72
      //   return
      // }
      if (this.calendarType === 'day') {
        setTimeout(() => {
          let max = 72

          if (this.calendarEvents.length) {
            const heights = this.calendarEvents
              .map(item => {
                const element = document.getElementById(`daily_${item.id}`)
                return element ? element.clientHeight : 0
              })
              .filter(Boolean)

            heights.sort((a, b) => b - a)

            if (heights.length) {
              max = heights[0]
            }
          }

          this.intervalH = Math.max(max, 72)

          setTimeout(() => {
            this._updateTimeline(jump)
          }, 100)
        }, 100)
        return
      }

      if (this.calendarType === 'week') {
        this.intervalH = 72
        return
      }
      // if (this.calendarType === 'day') {
      //   setTimeout(() => {
      //     let max = 20
      //     if (this.calendarEvents.length) {
      //       const pixelPerMinutes = this.calendarEvents
      //         .map(item => {
      //           const element = document.getElementById(`daily_${item.id}`)
      //           if (element) {
      //             const minutes = Math.abs(item.end.getTime() - item.start.getTime()) / (60 * 1000)
      //             if (minutes > 0 && element.clientHeight > 0) {
      //               return element.clientHeight / minutes
      //             }
      //           }
      //           return 0
      //         })
      //         .filter(Boolean)

      //       pixelPerMinutes.sort((a, b) => b - a)

      //       if (pixelPerMinutes.length) {
      //         max = pixelPerMinutes[0] * this.interval
      //       }
      //     }

      //     this.intervalH = max

      //     setTimeout(() => {
      //       this._updateTimeline(jump)
      //     }, 100)
      //   }, 100)
      //   return
      // }

      // if (this.calendarType === 'week') {
      //   setTimeout(() => {
      //     let max = 20
      //     if (this.calendarEvents.length) {
      //       const pixelPerMinutes = this.calendarEvents
      //         .map(item => {
      //           const element = document.getElementById(`week_${item.id}`)
      //           if (element) {
      //             const minutes = Math.abs(item.end.getTime() - item.start.getTime()) / (60 * 1000)
      //             if (minutes > 0 && element.clientHeight > 0) {
      //               return element.clientHeight / minutes
      //             }
      //           }
      //           return 0
      //         })
      //         .filter(Boolean)

      //       pixelPerMinutes.sort((a, b) => b - a)

      //       if (pixelPerMinutes.length) {
      //         max = pixelPerMinutes[0] * this.interval
      //       }
      //     }

      //     this.intervalH = Math.max(max, 72)

      //     setTimeout(() => {
      //       this._updateTimeline(jump)
      //     }, 100)
      //   }, 100)
      //   return
      // }
    },
    _updateTimeline(jump = true) {
      if (this.calendarType !== 'day') return
      this.currentY = '-10px'
      if (this.$refs && this.$refs.dayCalendar) {
        if (
          this.$validate.isDateEqual(
            this.$formatter.convertDate(this.calendarValue),
            this.$formatter.convertDate(this.$refs.dayCalendar.times.now.date),
          )
        ) {
          const y = this.$refs.dayCalendar.timeToY(this.$refs.dayCalendar.times.now)
          if (y > 0) {
            this.currentY = y + 'px'
          }

          if (jump) {
            this._scrollToTime()
          }
          this._updateTime()
        }
      }
    },
    _scrollToTime() {
      const time =
        this.$refs && this.$refs.dayCalendar
          ? this.$refs.dayCalendar.times.now.hour * 60 + this.$refs.dayCalendar.times.now.minute
          : 0

      const m =
        parseInt(this.$settings.startTime.substring(0, 2)) * 60 + parseInt(this.$settings.startTime.substring(3, 5))
      const d = time - m

      if (d > 0) {
        const container = document.getElementById('day-calendar-container')
        if (container) {
          const rect = container.getBoundingClientRect()
          const start = this.$formatter.convertDate('2026-01-01', this.$settings.startTime)
          const end = this.$formatter.convertDate('2026-01-01', this.$settings.endTime)
          const diff = Math.abs(end.getTime() - start.getTime()) / (60 * 1000)
          const pixelPerMinutes = rect.height / diff
          const top = rect.top + pixelPerMinutes * d
          window.scrollTo({ top: (window.scrollY || window.pageYOffset || 0) + top - 100 })
        }
      }
    },
    _updateTime() {
      if (!this.dayCalendarInterval) {
        this.dayCalendarInterval = setInterval(() => {
          if (this.$refs && this.$refs.dayCalendar) {
            this.$refs.dayCalendar.updateTimes()
          }
        }, 6 * 10000)
      }
    },
    _isToday(date) {
      const d = this.$formatter.convertDate(date)
      const today = this.$formatter.convertDate()
      return this.$validate.isDateEqual(d, today)
    },
    _layoutWeekEvents() {
      if (this.calendarType !== 'week') return

      const eventW = 185

      this.datesList.forEach(date => {
        const events = this.calendarEvents
          .filter(item => item.date === date)
          .sort((a, b) => this._compareEventTime(a, b))

        const columns = []

        events.forEach(event => {
          let placed = false
          let colIndex = 0

          for (let i = 0; i < columns.length; i++) {
            let hasConflict = false

            for (let j = 0; j < columns[i].length; j++) {
              const existing = columns[i][j]
              const overlap =
                event.start.getTime() < existing.end.getTime() &&
                event.end.getTime() > existing.start.getTime()

              if (overlap) {
                hasConflict = true
                break
              }
            }

            if (!hasConflict) {
              columns[i].push(event)
              colIndex = i
              placed = true
              break
            }
          }

          if (!placed) {
            columns.push([event])
            colIndex = columns.length - 1
          }

          const node = document.getElementById(`week_${event.id}`)
          if (node) {
            const parent = node.closest('.v-event-timed')
            if (parent) {
              parent.style.left = `${eventW * colIndex}px`
              parent.style.minWidth = `${eventW}px`
              parent.style.maxWidth = `${eventW}px`
              parent.style.width = `${eventW}px`
            }
          }
        })
      })
    },
    _updateWeekColumnWidths() {
      if (this.calendarType !== 'week') return

      this.$nextTick(() => {
        const root = this.$refs.weekCalendar && this.$refs.weekCalendar.$el
        if (!root) return

        setTimeout(() => {
          // const headerCells = root.querySelectorAll('[role="columnheader"]')
          // const bodyCells = root.querySelectorAll('.v-calendar-daily__day')
          const headerCells = root.querySelectorAll('.v-calendar-daily_head-day')
          const bodyCells = root.querySelectorAll('.v-calendar-daily__day')

          this.datesList.forEach((date, index) => {
            const count = this.weekColumnCountMap[date] || 1
            const width = count * 185

            if (headerCells[index]) {
              headerCells[index].style.width = `${width}px`
              headerCells[index].style.minWidth = `${width}px`
              headerCells[index].style.maxWidth = `${width}px`
              headerCells[index].style.flex = `0 0 ${width}px`
            }

            if (bodyCells[index]) {
              bodyCells[index].style.width = `${width}px`
              bodyCells[index].style.minWidth = `${width}px`
              bodyCells[index].style.maxWidth = `${width}px`
              bodyCells[index].style.flex = `0 0 ${width}px`
            }
          })
        }, 50)
      })
    },
    // _syncWeekHeaderScroll() {
    //   if (this.calendarType !== 'week') return

    //   this.$nextTick(() => {
    //     const calendar = this.$refs.weekCalendar && this.$refs.weekCalendar.$el
    //     if (!calendar) return

    //     const head = calendar.querySelector('.v-calendar-daily__head')
    //     if (!head) return

    //     const scrollTarget =
    //       calendar.querySelector('.v-calendar-daily__body') ||
    //       calendar.querySelector('.v-calendar-daily__scroll-area') ||
    //       calendar

    //     if (!scrollTarget) return

    //     const onScroll = () => {
    //       head.style.transform = `translateX(-${scrollTarget.scrollLeft}px)`
    //     }

    //     if (this.weekScrollTarget && this.weekScrollHandler) {
    //       this.weekScrollTarget.removeEventListener('scroll', this.weekScrollHandler)
    //     }

    //     this.weekScrollHandler = onScroll
    //     this.weekScrollTarget = scrollTarget
    //     scrollTarget.addEventListener('scroll', this.weekScrollHandler)

    //     onScroll()
    //   })
    // },
    _syncWeekHeaderScroll() {
      if (this.calendarType !== 'week') return

      this.$nextTick(() => {
        const calendar = this.$refs.weekCalendar && this.$refs.weekCalendar.$el
        if (!calendar) return

        const head = calendar.querySelector('.v-calendar-daily__head')
        const scrollTarget = calendar.querySelector('.v-calendar-daily__pane')

        if (!head || !scrollTarget) return

        const onScroll = () => {
          head.style.transform = `translateX(-${scrollTarget.scrollLeft}px)`
        }

        if (this.weekScrollTarget && this.weekScrollHandler) {
          this.weekScrollTarget.removeEventListener('scroll', this.weekScrollHandler)
        }

        this.weekScrollHandler = onScroll
        this.weekScrollTarget = scrollTarget
        scrollTarget.addEventListener('scroll', this.weekScrollHandler)

        onScroll()
      })
    },
  },
  mounted() {
    this._updateTimeline()
  },
  beforeDestroy() {
    if (this.weekScrollTarget && this.weekScrollHandler) {
      this.weekScrollTarget.removeEventListener('scroll', this.weekScrollHandler)
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep.custom-timetable {
  & > .custom-timetable__body {
    overflow-x: auto;
    box-sizing: border-box;

    & .custom-timetable__week-grid {
      width: 100%;
      min-width: 0;
    }

    & .custom-timetable__week-grid__header,
    & .custom-timetable__week-grid__row {
      display: flex;
      width: 100%;
    }

    & .custom-timetable__week-grid__header {
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    }

    & .custom-timetable__week-grid__row {
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    }

    & .custom-timetable__week-grid__body {
      width: 100%;
    }

    & .custom-timetable__week-grid__time-col {
      width: 96px;
      min-width: 96px;
      max-width: 96px;
      flex: 0 0 96px;
      padding: 12px 8px;
      text-align: center;
      box-sizing: border-box;
      border-right: 1px solid rgba(0, 0, 0, 0.08);
    }

    & .custom-timetable__week-grid__header .custom-timetable__week-grid__time-col {
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    }

    & .custom-timetable__week-grid__cell {
      width: auto;
      min-width: 150px;
      max-width: none;
      flex: 1 1 0;
      padding: 8px 4px;
      box-sizing: border-box;
      border-right: 1px solid rgba(0, 0, 0, 0.08);
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    }

    & .custom-timetable__week-grid .custom-timetable__col {
      width: auto;
      min-width: 150px;
      max-width: none;
      flex: 1 1 0;
    }

    & .custom-timetable__col {
      width: 185px;
      min-width: 185px;
      max-width: 185px;
      flex: 0 0 185px;
      position: relative;
      box-sizing: border-box;
      padding-left: 0;
      padding-right: 0;

      & * {
        box-sizing: border-box;
      }

      & .custom-timetable__col__header {
        font-size: 0.875rem;
        color: var(--v-colorBlack-base);
        text-align: center;
        padding-top: 16px;
        padding-bottom: 16px;
        display: block;
        width: 100%;
        position: relative;
        min-height: 84px;
        border-right: 1px solid rgba(0, 0, 0, 0.08);
      }

      & .custom-timetable__col__body {
        width: 100%;
        min-width: 185px;
        border-right: 1px solid rgba(0, 0, 0, 0.08);

        & > .custom-timetable__col-row {
          width: 100%;
          align-items: flex-start;
          position: relative;

          & > .custom-timetable__week-event {
            display: block;
            width: 185px;
            position: relative;
            margin-left: 1px;
            margin-right: 1px;

            &.squeeze {
              width: 100px !important;
            }
          }
        }
      }
    }
  }

  & .holiday-name {
    font-size: 0.75rem;
  }
}

::v-deep.v-calendar {
  & .v-event {
    height: auto !important;
    border-radius: 5px !important;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 5px !important;
  }

  & .v-event-timed {
    background: #eeeeee !important;
  }

  // & [role='columnheader'],
  // & [role='cell'] {
  //   min-width: 170px !important;
  // }

  & [role='cell'] {
    min-height: 100px !important;

    &.v-outside {
      & .v-calendar-weekly__day-label > .v-btn {
        opacity: 0.3;

        &:not(.holiday--text) {
          color: #58555d !important;
        }
      }

      & .holiday-name {
        opacity: 0.3;
      }
    }

    &.v-present {
      & .v-calendar-weekly__day-label > .v-btn {
        background-color: #efefef !important;
      }
    }
  }

  & [role='columnheader'] {
    font-size: 0.75rem !important;
    font-weight: 400;
    letter-spacing: 0.0333333333em !important;
    line-height: 1.25rem;
    font-family: 'Rubik', 'Noto Sans HK', sans-serif !important;
    padding-top: 10px;
    padding-bottom: 10px;
    color: var(--v-colorBlack-base) !important;
  }

  &.v-calendar-daily {
    & [role='columnheader'],
    & [role='cell'] {
      min-width: 185px !important;
    }

    & .v-event-timed {
      max-width: 185px !important;
      min-width: 185px !important;
    }

    & .v-calendar-daily_head-day {
      min-width: 185px !important;
    }

    & .v-calendar-daily__day {
      // border-right: none !important;
      min-width: 185px !important;
    }

    & .v-calendar-daily__intervals-body {
      & > .v-calendar-daily__interval:first-of-type {
        & > .v-calendar-daily__interval-text {
          padding-top: 10px !important;
        }
      }
    }

    & .v-calendar-daily__scroll-area {
      overflow-y: hidden !important;
    }
  }
}


::v-deep .v-calendar-weekly {
  .v-event-timed {
    min-width: 185px !important;
    max-width: 185px !important;
    width: 185px !important;
    // position: absolute !important;
    box-sizing: border-box !important;
  }

  .v-calendar-daily__head,
  .v-calendar-daily__week,
  .v-calendar-daily__body {
    width: max-content !important;
  }

  .v-calendar-daily__week {
    display: flex !important;
  }

  .v-calendar-daily__day,
  .v-calendar-daily__day-container,
  .v-calendar-daily__interval,
  .v-calendar-daily__interval-content {
    overflow: visible !important;
  }

  [role='columnheader'],
  [role='cell'] {
    min-width: auto !important;
  }
}


.custom-timetable__body {
  overflow-x: auto !important;
}

::v-deep .v-calendar-weekly [role='columnheader'],
::v-deep .v-calendar-weekly [role='cell'] {
  min-width: auto !important;
}

::v-deep .v-calendar-weekly .v-calendar-daily__day-container,
::v-deep .v-calendar-weekly .v-calendar-daily__day,
::v-deep .v-calendar-weekly .v-calendar-daily__interval,
::v-deep .v-calendar-weekly .v-calendar-daily__interval-content {
  overflow: visible !important;
}
::v-deep .v-calendar-weekly .v-calendar-daily__interval-content {
  position: relative !important;
}
::v-deep .v-calendar-weekly .v-calendar-daily__interval {
  height: auto !important;
  min-height: 72px !important;
}

::v-deep .v-calendar-weekly .v-calendar-daily__interval-content {
  height: auto !important;
  min-height: 72px !important;
  overflow: visible !important;
  position: relative !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: stretch !important;
}

::v-deep .v-calendar-weekly .v-calendar-daily__day-container,
::v-deep .v-calendar-weekly .v-calendar-daily__day {
  overflow: visible !important;
}

::v-deep .v-calendar-weekly .v-event,
::v-deep .v-calendar-weekly .v-event-timed {
  height: auto !important;
  min-height: 64px !important;
}

::v-deep .v-calendar-monthly [role='columnheader'],
::v-deep .v-calendar-monthly [role='cell'] {
  min-width: 150px !important;
}

::v-deep .week-event-wrap {
  display: block;
  width: 100%;
  min-height: 64px;
}

::v-deep .week-event-content {
  display: block;
  min-height: 64px;
}

::v-deep .v-calendar-weekly .v-calendar-daily__head {
  will-change: transform;
  position: relative;
  z-index: 2;
  background: white;
  width: max-content !important;
}

::v-deep .v-calendar .v-event:hover,
::v-deep .v-calendar .v-event-timed:hover {
  z-index: 999;
}

// ::v-deep.v-calendar {
//   & .v-event {
//     position: relative !important;
//     top: auto !important;
//     left: auto !important;
//     right: auto !important;
//     bottom: auto !important;
//     width: 100% !important;
//     max-width: 100% !important;
//     height: auto !important;
//     min-height: 64px;
//     margin: 0 0 6px 0 !important;
//     border-radius: 5px !important;
//     display: block !important;
//   }

//   & .v-event-timed {
//     background: #eeeeee !important;
//     position: relative !important;
//     transform: none !important;
//   }

//   & .v-calendar-daily__day-interval,
//   & .v-calendar-daily__interval {
//     height: auto !important;
//     min-height: 120px !important;
//   }

//   & .v-calendar-daily__interval-content {
//     height: auto !important;
//     min-height: 120px !important;
//     display: flex !important;
//     flex-direction: column !important;
//     align-items: stretch !important;
//     overflow: visible !important;
//   }

//   & .v-calendar-daily__day-container,
//   & .v-calendar-daily__day {
//     overflow: visible !important;
//   }

//   & .v-calendar-daily__scroll-area {
//     overflow-y: auto !important;
//   }
// }

.v-current-time {
  height: 2px;
  background-color: #ea4335;
  position: absolute;
  left: -1px;
  right: 0;
  pointer-events: none;

  &.first::before {
    content: '';
    position: absolute;
    background-color: #ea4335;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-top: -5px;
    margin-left: -6.5px;
  }
}

::v-deep.v-input--switch {
  & .v-input--switch__track {
    color: #ffffff !important;
    opacity: 1 !important;
  }

  & .v-label {
    color: #ffffff !important;
    font-size: 12px !important;
    font-weight: 500 !important;
  }
}

// ::v-deep.custom-timetable__week-grid {
//   // & .v-calendar-daily {
//     // background-color: #00f;
//     & .v-event-timed:not(:hover) {
//       height: 0px !important;
//       border: 0 !important;
//     }
//   // }
// }

</style>
